import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { CheckoutReducer } from '@app/pages/checkout-app/checkout-store/reducers';
import { CheckoutActions } from '@app/pages/checkout-app/checkout-store/actions';
import { onDestroyComplete } from '@app/sharedModule/decorators/on-destroy-unsubscribe.decorator';
import { Store } from '@ngrx/store';
import {
  CheckoutExtensionUI,
  CouponType,
  DiscountRejectionCouponExtension,
  ExtensionComponentType,
  ICheckoutExtensions,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { RuleOperator } from '@pdqdev/pdq-shared/dist/entities/Rules';
import { of, Subject } from 'rxjs';
import { catchError, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { ViewMode } from '../../../models';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
import { generateRandomString } from '@utils/string-utils';
import {
  DiscountRejectionCouponTab,
  DiscountRejectionCouponFormGroup,
  AppearanceFormGroup,
  ConfigurationFormGroup,
  WidgetAppearanceFormGroup,
  ButtonAppearanceFormGroup,
  FailedAttemptThresholdFormGroup,
  configurationGroupValidator,
} from './model';
import { FAILED_ATTEMPT_THRESHOLD_CONFIG } from './discount-rejection-coupon.config';
import { TypedRuleGroup } from '@app/sharedModule/components/rules-dynamic-form/model';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { ShopService } from '@app/services/shops/shop.service';
import { getExtensionRules } from '@pages/checkout-app/checkout-layout/main/components/extensions/shared/all-extension-rules';
import { ShopifyDiscountNode } from '@pages/checkout-app/services/extensions/shopify-discount.model';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
@Component({
  selector: 'discount-rejection-coupon',
  templateUrl: './discount-rejection-coupon.component.html',
  styleUrls: ['./discount-rejection-coupon.component.scss'],
  standalone: false,
})
export class DiscountRejectionCouponComponent implements OnInit, OnDestroy {
  @onDestroyComplete() destroy$ = new Subject();

  loading$ = this.store.select(CheckoutReducer.selectIsLoading);
  loadedExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  loadedExtension: ICheckoutExtensions;

  tabs = [
    { label: 'Settings', value: DiscountRejectionCouponTab.SETTINGS },
    { label: 'Configurations', value: DiscountRejectionCouponTab.CONFIGURATIONS },
    { label: 'Appearance', value: DiscountRejectionCouponTab.APPEARANCE },
    { label: 'Display Conditions', value: DiscountRejectionCouponTab.DISPLAY_CONDITIONS },
  ];
  DiscountRejectionCouponTab = DiscountRejectionCouponTab;
  activeTab = DiscountRejectionCouponTab.SETTINGS;
  formGroup: FormGroup<DiscountRejectionCouponFormGroup>;
  displayRules = getExtensionRules();
  displayRulesEntities = this.displayRules.map((r) => r.entity);
  shopCurrencySymbol: string;
  storeDiscountOptions: DropdownOption[] = [];
  private searchDiscount$ = new Subject<string>();

  constructor(
    private store: Store<CheckoutReducer.State>,
    private extensionsService: ExtensionsService,
    private rulesDynamicFormService: RulesDynamicFormService,
    private shopService: ShopService,
  ) {}

  ngOnDestroy() {}

  ngOnInit() {
    this.extensionsService.setCurrentExtensionType(ExtensionComponentType.DISCOUNT_REJECTION_COUPON);
    this.shopCurrencySymbol = this.shopService.shopCurrencySymbol;

    this.initForm();

    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((values) => {
      this.dispatchExampleViewValues(values);
    });

    this.formGroup.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.extensionsService.setFormValid(this.formGroup.valid);
    });

    this.extensionsService.setFormValid(this.formGroup.valid);

    this.loadedExtension$.pipe(takeUntil(this.destroy$)).subscribe((extension: ICheckoutExtensions) => {
      this.loadedExtension = extension;
      if (extension) {
        this.updateFormValues(extension.template, extension.usageToken);
      }
    });

    this.extensionsService.save$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.handleSave();
    });

    this.configurationsFormArray.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.resetWidgetTypeIfClickableDisabled();
    });

    this.loadStoreDiscounts();
  }

  private loadStoreDiscounts() {
    this.searchDiscount$
      .pipe(
        startWith(''),
        switchMap((term) => this.extensionsService.loadDiscountCodes(term).pipe(catchError(() => of([])))),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        const discounts = response ?? [];
        this.storeDiscountOptions = discounts
          .filter((node: ShopifyDiscountNode) => {
            const { title, summary } = node.discount;
            if (summary && (summary.toLowerCase().includes('for') || title.toLowerCase().includes('pdq'))) {
              return false;
            }
            return true;
          })
          .map((node: ShopifyDiscountNode) => this.mapDiscountToOption(node));
      });
  }

  onSearchDiscount(term: string) {
    this.searchDiscount$.next(term);
  }

  onTabChange(event: DiscountRejectionCouponTab) {
    this.activeTab = event;
  }

  get configurationsFormArray(): FormArray<FormGroup<ConfigurationFormGroup>> {
    return this.formGroup.controls.configurations;
  }

  get appearanceFormGroup(): FormGroup<AppearanceFormGroup> {
    return this.formGroup.controls.appearance;
  }

  get thresholdFormGroup(): FormGroup<FailedAttemptThresholdFormGroup> {
    return this.formGroup.controls.failedAttemptThreshold;
  }

  get isClickableDisabled(): boolean {
    const configs = this.configurationsFormArray.controls;
    if (configs.length === 0) return true;
    return configs.every(
      (c) => c.get('replacementDiscountRule')?.value === DiscountRejectionCouponExtension.ReplacementRule.NO_DISCOUNT,
    );
  }

  addConfiguration() {
    const isFirstConfig = this.configurationsFormArray.length === 0;
    const defaultRule = isFirstConfig ? RuleOperator.IS_ALL : RuleOperator.IS;

    this.configurationsFormArray.push(this.createConfigurationGroup(undefined, defaultRule));
    this.dispatchExampleViewValues(this.formGroup.value);
  }

  removeConfiguration(index: number) {
    this.configurationsFormArray.removeAt(index);
    this.dispatchExampleViewValues(this.formGroup.value);
  }

  private initForm() {
    this.formGroup = new FormGroup<DiscountRejectionCouponFormGroup>({
      usageToken: new FormControl('discount_rejection_coupon_' + generateRandomString(12), { nonNullable: true }),
      internalName: new FormControl('Discount Rejection Coupon', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      internalDescription: new FormControl('Coupon code rejection', { nonNullable: true }),
      configurations: new FormArray<FormGroup<ConfigurationFormGroup>>([]),
      appearance: this.createAppearanceGroup(),
      displayRules: this.rulesDynamicFormService.initRulesFormArray([], this.displayRules) as FormArray<
        FormGroup<TypedRuleGroup>
      >,
      failedAttemptThreshold: this.createThresholdGroup(),
    });

    this.dispatchExampleViewValues(this.formGroup.value);
  }

  private updateFormValues(loadedValues: DiscountRejectionCouponExtension.Template, usageToken: string) {
    this.formGroup.patchValue({
      usageToken: usageToken ?? 'discount_rejection_coupon_' + generateRandomString(12),
      internalName: loadedValues.internalName ?? 'Discount Rejection Coupon',
      internalDescription: loadedValues.internalDescription ?? 'Coupon code rejection',
    });

    this.formGroup.setControl(
      'displayRules',
      this.rulesDynamicFormService.initRulesFormArray(loadedValues.displayRules ?? [], this.displayRules) as FormArray<
        FormGroup<TypedRuleGroup>
      >,
    );

    const configurationsArray = this.configurationsFormArray;
    configurationsArray.clear();
    (loadedValues.configurations ?? []).forEach((config) => {
      configurationsArray.push(this.createConfigurationGroup(config));
    });

    this.formGroup.setControl('appearance', this.createAppearanceGroup(loadedValues.appearance));
    this.formGroup.setControl('failedAttemptThreshold', this.createThresholdGroup(loadedValues.failedAttemptThreshold));
    this.resetWidgetTypeIfClickableDisabled();

    this.dispatchExampleViewValues(this.formGroup.value);
  }

  private createConfigurationGroup(
    config?: DiscountRejectionCouponExtension.Configuration,
    defaultRule?: RuleOperator,
  ): FormGroup<ConfigurationFormGroup> {
    return new FormGroup<ConfigurationFormGroup>(
      {
        ruleTitle: new FormControl(config?.ruleTitle ?? 'New Rule', { nonNullable: true }),
        active: new FormControl(config?.active ?? true, { nonNullable: true }),
        couponType: new FormControl(config?.couponType ?? CouponType.PERCENTAGE, {
          nonNullable: true,
        }),
        couponRule: new FormControl(config?.couponRule ?? defaultRule ?? RuleOperator.IS_ALL, { nonNullable: true }),
        couponCode: new FormControl(config?.couponCode ?? '', { nonNullable: true }),
        discountValue: new FormControl(config?.discountValue ?? 1, { nonNullable: true }),
        couponExpiryDays: new FormControl(config?.couponExpiryDays ?? 30, { nonNullable: true }),
        autoApply: new FormControl(config?.autoApply ?? false, { nonNullable: true }),
        combinesWithProductDiscounts: new FormControl(config?.combinesWithProductDiscounts ?? false, {
          nonNullable: true,
        }),
        combinesWithOrderDiscounts: new FormControl(config?.combinesWithOrderDiscounts ?? false, { nonNullable: true }),
        combinesWithShippingDiscounts: new FormControl(config?.combinesWithShippingDiscounts ?? false, {
          nonNullable: true,
        }),
        messageTitle: new FormControl(config?.messageTitle ?? '', { nonNullable: true }),
        messageDescription: new FormControl(config?.messageDescription ?? '', { nonNullable: true }),
        replacementDiscountRule: new FormControl(
          config?.replacementDiscountRule ?? DiscountRejectionCouponExtension.ReplacementRule.NO_DISCOUNT,
          { nonNullable: true },
        ),
        storeDiscount: new FormControl(config?.storeDiscount ?? '', { nonNullable: true }),
      },
      { validators: [configurationGroupValidator] },
    );
  }

  private createAppearanceGroup(
    appearance?: DiscountRejectionCouponExtension.ExtensionAppearance,
  ): FormGroup<AppearanceFormGroup> {
    return new FormGroup<AppearanceFormGroup>({
      widgetType: new FormControl(appearance?.widgetType ?? DiscountRejectionCouponExtension.WidgetType.BANNER, {
        nonNullable: true,
      }),
      widgetAppearance: new FormGroup<WidgetAppearanceFormGroup>({
        backgroundColor: new FormControl(
          appearance?.widgetAppearance?.backgroundColor ?? CheckoutExtensionUI.Appearance.INFO,
          {
            nonNullable: true,
          },
        ),
        textColor: new FormControl(
          appearance?.widgetAppearance?.textColor ?? DiscountRejectionCouponExtension.BannerTextColorOptions.DEFAULT,
          {
            nonNullable: true,
          },
        ),
      }),
      buttonAppearance: new FormGroup<ButtonAppearanceFormGroup>({
        buttonCtaText: new FormControl(appearance?.buttonAppearance?.buttonCtaText ?? 'Claim', { nonNullable: true }),
        buttonAppearance: new FormControl(
          appearance?.buttonAppearance?.buttonAppearance ?? CheckoutExtensionUI.Appearance.MONOCHROME,
          { nonNullable: true },
        ),
      }),
    });
  }

  private createThresholdGroup(
    threshold?: DiscountRejectionCouponExtension.FailedAttemptThreshold,
  ): FormGroup<FailedAttemptThresholdFormGroup> {
    return new FormGroup<FailedAttemptThresholdFormGroup>({
      enabled: new FormControl(threshold?.enabled ?? FAILED_ATTEMPT_THRESHOLD_CONFIG.DEFAULT_ENABLED, {
        nonNullable: true,
      }),
      attempts: new FormControl(threshold?.attempts ?? FAILED_ATTEMPT_THRESHOLD_CONFIG.MIN_ATTEMPTS, {
        nonNullable: true,
      }),
      limitWithinTimeWindow: new FormControl(
        threshold?.limitWithinTimeWindow ?? FAILED_ATTEMPT_THRESHOLD_CONFIG.DEFAULT_LIMIT_WITHIN_TIME_WINDOW,
        { nonNullable: true },
      ),
      timeWindowValue: new FormControl(
        threshold?.timeWindowValue ?? FAILED_ATTEMPT_THRESHOLD_CONFIG.DEFAULT_TIME_WINDOW_MINUTES,
        { nonNullable: true },
      ),
      timeWindowUnit: new FormControl(
        threshold?.timeWindowUnit ?? DiscountRejectionCouponExtension.TimeWindowUnit.MINUTES,
        { nonNullable: true },
      ),
    });
  }

  private dispatchExampleViewValues(values: FormGroup<DiscountRejectionCouponFormGroup>['value']) {
    const mode = this.loadedExtension ? ViewMode.EDIT : ViewMode.NEW;
    const data = {
      [ExtensionComponentType.DISCOUNT_REJECTION_COUPON]: {
        mode,
        template: values,
      },
    };
    this.store.dispatch(CheckoutActions.liveExampleTemplateView({ data }));
  }

  private resetWidgetTypeIfClickableDisabled() {
    if (this.isClickableDisabled) {
      this.appearanceFormGroup.controls.widgetType.setValue(DiscountRejectionCouponExtension.WidgetType.BANNER);
    }
  }

  private handleSave() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.configurationsFormArray.controls.forEach((control) => {
        control.markAllAsTouched();
      });
      return;
    }

    const formValue = JSON.parse(JSON.stringify(this.formGroup.value));

    if (formValue.appearance?.widgetType === DiscountRejectionCouponExtension.WidgetType.BANNER) {
      delete formValue.appearance.buttonAppearance;
    }

    const { usageToken, ...template } = formValue;
    const payload: Partial<ICheckoutExtensions> = {
      template,
      usageToken,
      type: ExtensionComponentType.DISCOUNT_REJECTION_COUPON,
    };

    if (this.loadedExtension?.id) {
      const id = this.loadedExtension.id;
      this.store.dispatch(CheckoutActions.updateExtension({ id, extension: payload }));
    } else {
      this.store.dispatch(CheckoutActions.saveExtension({ extension: payload }));
    }
  }

  duplicateConfiguration(index: number) {
    const sourceConfig = this.configurationsFormArray.at(index).getRawValue();
    const duplicatedConfig = { ...sourceConfig, ruleTitle: sourceConfig.ruleTitle + ' (Copy)' };
    this.configurationsFormArray.push(this.createConfigurationGroup(duplicatedConfig));
    this.dispatchExampleViewValues(this.formGroup.value);
  }
  private mapDiscountToOption(node: ShopifyDiscountNode): DropdownOption {
    const { title, status, customerGets } = node.discount;
    const discountValue = customerGets?.value;
    let valueSuffix = '';

    if (discountValue?.__typename === 'DiscountPercentage') {
      valueSuffix = ` - ${discountValue.percentage * 100}%`;
    } else if (discountValue?.amount) {
      valueSuffix = ` - ${this.shopCurrencySymbol}${discountValue.amount.amount}`;
    }

    return {
      value: title,
      label: `${title} (${status.toLowerCase()})${valueSuffix}`,
    };
  }
}
